<template>
  <div class="agent-bubble" :class="`agent-bubble--${role}`">
    <!-- 用户消息 -->
    <div v-if="role === 'user'" class="agent-bubble__user-row">
      <div
        v-if="status === 'failed'"
        class="agent-bubble__retry-btn"
        title="重新发送"
        @click="$emit('retry')"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"></polyline>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
      </div>
      <div class="agent-bubble__content agent-bubble__content--user" :class="{ 'agent-bubble__content--failed': status === 'failed' }">
        <!-- 附件列表：图片显示缩略图，文档显示标签 -->
        <div v-if="attachments && attachments.length > 0" class="agent-bubble__attachments">
          <template v-for="(att, idx) in attachments">
            <!-- 图片附件：缩略图，点击大图预览 -->
            <div
              v-if="att.mimeType && att.mimeType.startsWith('image/')"
              :key="'img-' + idx"
              class="agent-bubble__attachment-img-wrap"
              :title="att.fileName"
              @click="openPreview(att)"
            >
              <img
                :src="'data:' + att.mimeType + ';base64,' + att.data"
                class="agent-bubble__attachment-thumb"
                :alt="att.fileName"
              />
              <span class="agent-bubble__attachment-img-zoom">🔍</span>
            </div>
            <!-- 文档附件：标签 -->
            <span
              v-else
              :key="'doc-' + idx"
              class="agent-bubble__attachment-tag"
            >
              {{ resolveFileIcon(att.mimeType) }} {{ att.fileName }}
            </span>
          </template>
        </div>
        <span v-if="text" class="agent-bubble__user-text">{{ text }}</span>
      </div>
    </div>

    <!-- AI 消息 -->
    <div v-else class="agent-bubble__agent-row">
      <!-- AI 头像 -->
      <div class="agent-bubble__avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#0052D9" opacity="0.12"/>
          <path d="M12 6C9.79 6 8 7.79 8 10s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" fill="#0052D9"/>
        </svg>
      </div>

      <div class="agent-bubble__agent-content">
        <!-- 进度提示（无正文时显示） -->
        <div v-if="progressText && !text" class="agent-bubble__progress">
          <span class="agent-bubble__progress-spinner"></span>
          <span class="agent-bubble__progress-text">{{ progressText }}</span>
        </div>

        <!-- 正文气泡 -->
        <div
          v-if="hasVisibleText"
          class="agent-bubble__content agent-bubble__content--agent agent-markdown"
          :class="{ 'agent-bubble__content--streaming': !done }"
          v-html="renderedText"
        ></div>

        <!-- 卡片列表（按 order 字段统一排序渲染，保证 AI 推送顺序） -->
        <template v-if="cards && cards.length">
          <div class="agent-bubble__cards">
            <template v-for="card in sortedCards">
              <QuickOptionsCard
                v-if="card.cardType === 'quick-options'"
                :key="card.id"
                :card="card.cardData"
                @action="onCardAction"
              />
              <TextInputCard
                v-else-if="card.cardType === 'text-input'"
                :key="card.id"
                :card="card.cardData"
                @action="onCardAction"
              />
              <MultiSelectOptionsCard
                v-else-if="card.cardType === 'multi-select-options'"
                :key="card.id"
                :card="card.cardData"
                @action="onCardAction"
              />
              <WriteApprovalCard
                v-else-if="card.cardType === 'write-approval'"
                :key="card.id"
                :approval="card.cardData"
                @decision="onApprovalDecision"
              />
              <NavigationCard
                v-else-if="card.cardType === 'navigate'"
                :key="card.id"
                :nav="card.cardData"
              />
            </template>
          </div>
        </template>

        <!-- 进度提示（有正文时附在下方） -->
        <div v-if="progressText && text && !done" class="agent-bubble__progress agent-bubble__progress--inline">
          <span class="agent-bubble__progress-spinner"></span>
          <span class="agent-bubble__progress-text">{{ progressText }}</span>
        </div>

        <!-- 流式呼吸动画 -->
        <div v-if="!done && !progressText && hasVisibleText" class="agent-bubble__pulse">
          <span class="agent-bubble__pulse-dot"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api';
import WriteApprovalCard from './WriteApprovalCard.vue';
import NavigationCard from './NavigationCard.vue';
import QuickOptionsCard from './QuickOptionsCard.vue';
import MultiSelectOptionsCard from './MultiSelectOptionsCard.vue';
import TextInputCard from './TextInputCard.vue';

/**
 * 极简 Markdown 渲染（纯文字场景，不引入额外依赖）
 * 支持：粗体、斜体、行内代码、代码块、标题、列表、分割线、段落
 */
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    // 转义 HTML 特殊字符（防 XSS）
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 代码块
  html = html.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    return `<pre class="agent-code-block"><code>${code.trim()}</code></pre>`;
  });

  // 行内代码
  html = html.replace(/`([^`]+)`/g, '<code class="agent-inline-code">$1</code>');

  // 标题
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // 粗体 + 斜体
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // 分割线
  html = html.replace(/^---+$/gm, '<hr>');

  // Markdown 表格
  html = html.replace(/((?:^\|.+\|\n?)+)/gm, (match) => {
    const lines = match.trim().split('\n').filter((l) => l.trim());
    if (lines.length < 2) return match;
    // 第二行是分隔行（:---:|:---:）
    const sepLine = lines[1];
    if (!/^\|[\s|:\-]+\|$/.test(sepLine)) return match;

    const parseRow = (line) =>
      line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

    const headers = parseRow(lines[0]);
    const dataRows = lines.slice(2);

    const thead = '<thead><tr>' + headers.map((h) => `<th>${h}</th>`).join('') + '</tr></thead>';
    const tbody = '<tbody>' + dataRows.map((row) => {
      const cells = parseRow(row);
      return '<tr>' + cells.map((c) => `<td>${c}</td>`).join('') + '</tr>';
    }).join('') + '</tbody>';

    return `<table class="agent-md-table">${thead}${tbody}</table>`;
  });

  // 无序列表（连续行合并为 ul）
  html = html.replace(/((?:^[*\-] .+\n?)+)/gm, (match) => {
    const items = match.trim().split('\n').map((line) => {
      return `<li>${line.replace(/^[*\-] /, '')}</li>`;
    }).join('');
    return `<ul>${items}</ul>`;
  });

  // 有序列表
  html = html.replace(/((?:^\d+\. .+\n?)+)/gm, (match) => {
    const items = match.trim().split('\n').map((line) => {
      return `<li>${line.replace(/^\d+\. /, '')}</li>`;
    }).join('');
    return `<ol>${items}</ol>`;
  });

  // 段落（双换行）
  html = html.replace(/\n\n+/g, '</p><p>');
  // 单换行
  html = html.replace(/\n/g, '<br>');

  // 包裹段落
  if (!html.startsWith('<')) {
    html = `<p>${html}</p>`;
  }

  return html;
}

export default {
  name: 'AgentMessageBubble',
  components: { WriteApprovalCard, NavigationCard, QuickOptionsCard, TextInputCard, MultiSelectOptionsCard },
  props: {
    role: {
      type: String,
      required: true, // 'user' | 'agent'
    },
    text: {
      type: String,
      default: '',
    },
    done: {
      type: Boolean,
      default: true,
    },
    status: {
      type: String,
      default: undefined, // 'pending' | 'failed'
    },
    progressText: {
      type: String,
      default: '',
    },
    /** 卡片列表，由 useAgentChat 解析后传入 */
    cards: {
      type: Array,
      default: () => [],
    },
    /** 附件元信息列表（仅用于展示，不含 base64 数据） */
    attachments: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['retry', 'approval-decision', 'card-action'],
  setup(props, { emit }) {
    const hasVisibleText = computed(
      () => (props.text || '').trim().length > 0,
    );

    const renderedText = computed(() => renderMarkdown(props.text));

    /** 所有卡片按 order 字段升序排列，保证 AI 推送顺序正确 */
    const sortedCards = computed(() => {
      return [...(props.cards || [])].sort((a, b) => {
        const oa = a.order ?? 0;
        const ob = b.order ?? 0;
        return oa - ob;
      });
    });

    function onApprovalDecision(approvalId, decision) {
      emit('approval-decision', approvalId, decision);
    }

    function onCardAction(cardId, action, value) {
      emit('card-action', cardId, action, value);
    }

    function resolveFileIcon(mimeType) {
      if (mimeType === 'application/pdf') return '📄';
      if (mimeType === 'text/plain') return '📝';
      if (mimeType && mimeType.startsWith('image/')) return '🖼️';
      return '📃'; // Word
    }

    // 大图预览：动态挂载到 document.body，避免父容器 transform 影响 fixed 定位
    let previewEl = null;

    function openPreview(att) {
      closePreview(); // 先清理旧的

      // 创建遮罩容器（样式全部内联，不依赖外部 CSS）
      const overlay = document.createElement('div');
      Object.assign(overlay.style, {
        position: 'fixed',
        inset: '0',
        top: '0', left: '0', right: '0', bottom: '0',
        zIndex: '99999',
        background: 'rgba(0,0,0,0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: '0',
        transition: 'opacity 0.2s',
        cursor: 'zoom-out',
      });

      // 点击遮罩关闭
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closePreview();
      });

      // 内容盒子
      const box = document.createElement('div');
      Object.assign(box.style, {
        position: 'relative',
        maxWidth: '90vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        cursor: 'default',
      });

      // 关闭按钮
      const closeBtn = document.createElement('button');
      closeBtn.textContent = '✕';
      closeBtn.title = '关闭（ESC）';
      Object.assign(closeBtn.style, {
        position: 'absolute',
        top: '-38px',
        right: '-4px',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255,255,255,0.18)',
        color: '#fff',
        fontSize: '15px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1',
      });
      closeBtn.addEventListener('mouseenter', () => { closeBtn.style.background = 'rgba(255,255,255,0.35)'; });
      closeBtn.addEventListener('mouseleave', () => { closeBtn.style.background = 'rgba(255,255,255,0.18)'; });
      closeBtn.addEventListener('click', closePreview);

      // 图片
      const img = document.createElement('img');
      img.src = `data:${att.mimeType};base64,${att.data}`;
      img.alt = att.fileName;
      Object.assign(img.style, {
        maxWidth: '90vw',
        maxHeight: '80vh',
        borderRadius: '10px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        objectFit: 'contain',
        display: 'block',
      });

      // 文件名
      const name = document.createElement('div');
      name.textContent = att.fileName;
      Object.assign(name.style, {
        color: 'rgba(255,255,255,0.8)',
        fontSize: '13px',
        maxWidth: '80vw',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
      });

      box.appendChild(closeBtn);
      box.appendChild(img);
      box.appendChild(name);
      overlay.appendChild(box);
      document.body.appendChild(overlay);
      previewEl = overlay;

      // ESC 关闭
      const onKeydown = (e) => {
        if (e.key === 'Escape') { closePreview(); document.removeEventListener('keydown', onKeydown); }
      };
      document.addEventListener('keydown', onKeydown);

      // 淡入动画
      requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    }

    function closePreview() {
      if (!previewEl) return;
      const el = previewEl;
      previewEl = null;
      el.style.opacity = '0';
      setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
    }

    return { hasVisibleText, renderedText, sortedCards, onApprovalDecision, onCardAction, resolveFileIcon, openPreview, closePreview };
  },
};
</script>

<style scoped>
.agent-bubble {
  display: flex;
  margin: 10px 16px;
}

/* ---- 用户消息 ---- */
.agent-bubble--user {
  justify-content: flex-end;
}
.agent-bubble__user-row {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  max-width: 78%;
}
.agent-bubble__retry-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  color: #f53f3f;
  cursor: pointer;
  flex-shrink: 0;
}
.agent-bubble__retry-btn svg {
  width: 13px;
  height: 13px;
}
.agent-bubble__content--user {
  background: linear-gradient(135deg, #0052D9 0%, #2979FF 100%);
  color: #fff;
  border-radius: 16px 16px 4px 16px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.25);
}
.agent-bubble__content--failed {
  background: #fff2f0 !important;
  color: #333 !important;
  border: 1px solid #ffccc7;
  box-shadow: none !important;
}
.agent-bubble__user-text {
  white-space: pre-wrap;
}
/* 附件列表 */
.agent-bubble__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
  align-items: flex-end;
}
/* 图片缩略图 */
.agent-bubble__attachment-img-wrap {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  transition: border-color 0.2s, transform 0.15s;
}
.agent-bubble__attachment-img-wrap:hover {
  border-color: #fff;
  transform: scale(1.04);
}
.agent-bubble__attachment-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.agent-bubble__attachment-img-zoom {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.2s;
}
.agent-bubble__attachment-img-wrap:hover .agent-bubble__attachment-img-zoom {
  opacity: 1;
}
.agent-bubble__attachment-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 12px;
  color: #fff;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---- AI 消息 ---- */
.agent-bubble--agent {
  justify-content: flex-start;
}
.agent-bubble__agent-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 88%;
  width: 88%;
}
.agent-bubble__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f0f4ff;
  border: 1px solid #dce6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.agent-bubble__agent-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}
.agent-bubble__cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}
.agent-bubble__content--agent {
  background: #fff;
  color: #1a202c;
  border-radius: 4px 16px 16px 16px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f2f5;
}
.agent-bubble__content--streaming {
  border-bottom-color: #0052D9;
}

/* ---- 进度提示 ---- */
.agent-bubble__progress {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #fff;
  border-radius: 4px 16px 16px 16px;
  border: 1px solid #f0f2f5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.agent-bubble__progress--inline {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 4px 0 0;
}
.agent-bubble__progress-spinner {
  width: 12px;
  height: 12px;
  border: 1.5px solid #d8dbe0;
  border-top-color: #0052D9;
  border-radius: 50%;
  animation: agent-spin 0.8s linear infinite;
  flex-shrink: 0;
}
.agent-bubble__progress-text {
  font-size: 13px;
  color: #86909c;
}

/* ---- 流式呼吸动画 ---- */
.agent-bubble__pulse {
  display: flex;
  align-items: center;
  padding: 2px 0;
}
.agent-bubble__pulse-dot {
  display: inline-block;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #0052D9;
  animation: agent-pulse 1.4s ease-in-out infinite;
}
@keyframes agent-pulse {
  0%, 100% { opacity: 0.3; transform: scaleX(0.6); }
  50% { opacity: 1; transform: scaleX(1); }
}
@keyframes agent-spin {
  to { transform: rotate(360deg); }
}

/* ---- 大图预览 overlay（body 级别，不受父容器 transform 影响） ---- */
.agent-body-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.agent-body-preview-overlay--visible {
  opacity: 1;
}
.agent-body-preview-box {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.agent-body-preview-img {
  max-width: 90vw;
  max-height: 80vh;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  object-fit: contain;
  display: block;
}
.agent-body-preview-name {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  max-width: 80vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.agent-body-preview-close {
  position: absolute;
  top: -36px;
  right: -4px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  line-height: 1;
}
.agent-body-preview-close:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>

<!-- Markdown 渲染内容的全局样式（不加 scoped，因为 v-html 内容不受 scoped 影响） -->
<style>
.agent-markdown h1, .agent-markdown h2, .agent-markdown h3 {
  margin: 10px 0 5px;
  font-weight: 600;
  color: #1a202c;
}
.agent-markdown h1 { font-size: 17px; }
.agent-markdown h2 { font-size: 15px; }
.agent-markdown h3 { font-size: 14px; }
.agent-markdown p {
  margin: 0;
  line-height: 1.7;
}
.agent-markdown p + p { margin-top: 6px; }
.agent-markdown ul, .agent-markdown ol {
  padding-left: 18px;
  margin: 4px 0;
}
.agent-markdown li { margin: 3px 0; line-height: 1.6; }
.agent-markdown hr { margin: 8px 0; border: none; border-top: 1px solid #e5e6eb; }
.agent-markdown strong { font-weight: 600; }
.agent-markdown em { font-style: italic; }
.agent-inline-code {
  background: #f5f7fa;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 13px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  color: #e34d59;
}
.agent-code-block {
  background: #1e2433;
  color: #e0e0e0;
  padding: 12px 14px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
  font-size: 13px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  line-height: 1.6;
}
.agent-code-block code {
  background: transparent;
  color: inherit;
  padding: 0;
}
/* 表格样式 */
.agent-md-table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
  font-size: 13px;
  line-height: 1.5;
}
.agent-md-table th,
.agent-md-table td {
  border: 1px solid #e5e6eb;
  padding: 6px 10px;
  text-align: left;
  word-break: break-word;
}
.agent-md-table thead tr {
  background: #f5f7fa;
}
.agent-md-table th {
  font-weight: 600;
  color: #1a202c;
}
.agent-md-table tbody tr:nth-child(even) {
  background: #fafbfc;
}
.agent-md-table tbody tr:hover {
  background: #f0f4ff;
}
</style>
